import React, { useEffect, useState } from 'react';
import { Send, X } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Contingency = () => {
  const { darkMode } = useSettings();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [manualMessage, setManualMessage] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await fetch(
          `${API_URL}/contingency/dashboard`,
          {
            credentials: 'include'
          }
        );
      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const openInterventionModal = (lead) => {
    setSelectedLead(lead);
    setShowModal(true);
  };

  const sendManualMessage = async () => {
    if (!selectedLead || !manualMessage.trim()) return;

    try {
      const response = await fetch(`${API_URL}/contingency/leads/${selectedLead.id}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: manualMessage })
      });
      const result = await response.json();
      if (result.success) {
        alert('Mensagem enviada com sucesso!');
        setManualMessage('');
        setShowModal(false);
        loadDashboardData();
      }
    } catch (error) {
      alert('Erro ao enviar mensagem');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-500">Carregando dashboard de contingência...</div>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const alerts = dashboardData?.critical_alerts || [];
  const activeLeads = dashboardData?.active_leads || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          🚨 Dashboard de Contingência Humana
        </h1>
        <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
          Monitoramento em tempo real de leads qualificados travados
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>Total de Leads (24h)</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats.total_leads || 0}
          </p>
        </div>
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>Novos</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats.novos || 0}
          </p>
        </div>
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>Aguardando Pagamento</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats.aguardando_pagamento || 0}
          </p>
        </div>
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>Pagos</p>
          <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats.pagos || 0}
          </p>
        </div>
      </div>

      {/* Critical Alerts */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          ⚠️ Alertas Críticos (Leads Qualificados Travados &gt; 10min)
        </h2>
        {alerts.length === 0 ? (
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Nenhum alerta crítico no momento.</p>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border ${
                  alert.alert_level === 'critical'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {alert.nome || 'Sem nome'} - {alert.telefone}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Status: {alert.status}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Tempo sem atividade: {alert.time_without_activity} minutos
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Última atividade: {new Date(alert.last_activity).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <button
                    onClick={() => openInterventionModal(alert)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Assumir Controle
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Leads Table */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          📋 Leads Ativos
        </h2>
        {activeLeads.length === 0 ? (
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Nenhum lead ativo no momento.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-sm border-b ${darkMode ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200'}`}>
                  <th className="pb-3">Nome</th>
                  <th className="pb-3">Telefone</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Última Atividade</th>
                  <th className="pb-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {activeLeads.map((lead) => (
                  <tr key={lead.id} className={`border-b last:border-0 ${darkMode ? 'border-gray-700' : ''}`}>
                    <td className={`py-3 font-medium ${darkMode ? 'text-white' : ''}`}>
                      {lead.nome || 'Não informado'}
                    </td>
                    <td className={`py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {lead.telefone}
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.status === 'pago' ? 'bg-green-100 text-green-800' :
                        lead.status === 'aguardando_pagamento' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className={`py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {lead.last_activity ? new Date(lead.last_activity).toLocaleString('pt-BR') : '-'}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => openInterventionModal(lead)}
                        className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Intervir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Intervention Modal */}
      {showModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-lg w-full mx-4`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Intervenção Manual
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X size={20} className={darkMode ? 'text-white' : 'text-gray-900'} />
              </button>
            </div>
            <div className="space-y-3 mb-4">
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                <strong>Nome:</strong> {selectedLead.nome || 'Não informado'}
              </p>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                <strong>Telefone:</strong> {selectedLead.telefone}
              </p>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                <strong>Status:</strong> {selectedLead.status}
              </p>
            </div>
            <textarea
              value={manualMessage}
              onChange={(e) => setManualMessage(e.target.value)}
              placeholder="Digite sua mensagem manual para o cliente..."
              className={`w-full p-3 rounded-lg border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={sendManualMessage}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Send size={18} />
                Enviar Mensagem
              </button>
              <button
                onClick={() => setShowModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contingency;
